import React from "react";
import {
	calculateFeeEstimate,
	calculateSinglePtFeeEstimate,
} from "./feeCalculation.ts";
import {
	determineSessionFundingStatus,
	determineEventFundingStatus,
} from "./fundingStatus.ts";
// import { detectRecurringEvents } from "./eventPatterns.ts";

export const contentToDisplay = (
	userSettings,
	eventInfo,
	currentLens,
	schedules,
) => {
	const eProps = eventInfo.event.extendedProps;

	// Define checks for view types
	const isDayGridMonth = eventInfo.view.type === "dayGridMonth";
	const isTimeGridWeek = eventInfo.view.type === "timeGridWeek";
	const isTimeGridDay = eventInfo.view.type === "timeGridDay"; // Daily view
	const isListView = eventInfo.view.type === "fourWeek"; // Adjust if using a different list view type

	// Filter out personal events here, only continue for sessions and regular sessions.
	const isPersonalEvent = eProps?.isPersonalEvent === true;
	if (isPersonalEvent) {
		if (currentLens !== "main") {
			return {
				content: null,
				backgroundColour: "transparent",
			};
		}

		const personalTitle = eventInfo.event.title;

		return {
			content: isDayGridMonth ? (
				eventInfo.timeText ? (
					`${formatMonthlyViewTime(eventInfo.timeText)} - ${personalTitle}`
				) : (
					personalTitle
				)
			) : isTimeGridWeek ? (
				<>
					{eventInfo.timeText}
					<br />
					{personalTitle}
				</>
			) : isTimeGridDay ? (
				<>
					{eventInfo.timeText ? `${eventInfo.timeText} - ` : ""}
					<b>{personalTitle}</b>
				</>
			) : isListView ? (
				eventInfo.timeText ? (
					`${eventInfo.timeText} - ${personalTitle}`
				) : (
					personalTitle
				)
			) : eventInfo.timeText ? (
				`${eventInfo.timeText} ${personalTitle}`
			) : (
				personalTitle
			),
			backgroundColour: setEventColor(userSettings, "main", eProps),
		};
	}

	// CONTINUE with sessions and regular sessions here.
	const hospitalName = eProps.hospital;
	const hospitalAcronym = eProps.hospital.acronym
		? eProps.hospital.acronym
		: abbreviateName(eProps.hospital.name);

	const surgeonName =
		eProps.surgeon.first_name + " " + eProps.surgeon.last_name;
	const surgeonAcronym = eProps.surgeon.acronym
		? eProps.surgeon.acronym
		: abbreviateName(
				eProps.surgeon.first_name + " " + eProps.surgeon.last_name,
			);

	const sessionFeeEstimate = calculateFeeEstimate(
		userSettings,
		eProps.events,
		schedules,
	);

	const timeGridDayTemplate = (
		<div className="event-multi-box">
			<div className="event-header">
				<div className="event-info">
					{eProps.startingTime.replace(":", "")} -
					<b>{` ${eProps.surgeon.first_name} ${eProps.surgeon.last_name}`}</b>{" "}
					- {eProps.hospital.name}
				</div>
				<div className="event-fees">
					<div className="session-fee-estimate">
						{sessionFeeEstimate
							? `Estimate $${sessionFeeEstimate.toFixed(2)}`
							: ""}
					</div>
					<div className="session-fee-actual">
						{sessionFeeEstimate
							? `Actual $${sessionFeeEstimate.toFixed(2)}`
							: ""}
					</div>
				</div>
			</div>
			{eProps.events.map((subEvent, index) => (
				<div
					key={index}
					className="event-box"
					style={{
						height: `${100 / eProps.events.length}%`, // Dynamically set height based on number of events
					}}
				>
					<div>
						<b>
							{subEvent.patient.title}{" "}
							{subEvent.patient.first_name}{" "}
							{subEvent.patient.last_name}
						</b>
						{` ${subEvent.patient.date_of_birth}`}
					</div>
					<div>{subEvent.procedure}</div>
				</div>
			))}
		</div>
	);

	// Define the content to display conditionally
	switch (currentLens) {
		case "main": {
			return {
				content: isDayGridMonth ? (
					`${
						formatMonthlyViewTime(eventInfo.timeText)
							? formatMonthlyViewTime(eventInfo.timeText) + " - "
							: ""
					}${hospitalAcronym} - ${surgeonAcronym}`
				) : isTimeGridWeek ? (
					<>
						{eventInfo.timeText} <br />
						{eProps.hospital.name} <br />
						{eProps.surgeon.first_name +
							" " +
							eProps.surgeon.last_name}
					</>
				) : isTimeGridDay ? (
					timeGridDayTemplate // Use the reusable template
				) : isListView ? (
					`${eventInfo.timeText} - ${hospitalAcronym} - ${surgeonAcronym}`
				) : (
					`${eventInfo.timeText} ${eventInfo.event.title}`
				),
				backgroundColour: setEventColor(userSettings, "main", eProps),
			};
		}
		case "uploadStatus": {
			return {
				content: isDayGridMonth
					? `${surgeonAcronym} - ${hospitalAcronym}`
					: isTimeGridWeek
						? `${eventInfo.timeText} - ${hospitalAcronym}`
						: isTimeGridDay
							? timeGridDayTemplate // Use the reusable template
							: isListView
								? `${surgeonAcronym} - ${hospitalAcronym}`
								: `${eventInfo.timeText} ${eventInfo.event.title}`,
				backgroundColour: setEventColor(
					userSettings,
					"uploadStatus",
					eProps,
				),
			};
		}
		case "feeEstimate": {
			const estimate = calculateFeeEstimate(
				userSettings,
				eProps.events,
				schedules,
			);

			return {
				content: estimate ? (
					isDayGridMonth ? (
						<>
							<b>${parseFloat(estimate.toFixed(2))}</b>,{" "}
							{eProps.events?.length}x Pt. - {surgeonAcronym}
						</>
					) : isTimeGridWeek ? (
						`${
							eventInfo.timeText
						} - ${surgeonAcronym} - Fee Estimate: $${estimate.toFixed(
							2,
						)}`
					) : isTimeGridDay ? (
						timeGridDayTemplate // Use the reusable template
					) : isListView ? (
						<>
							<b>${parseFloat(estimate.toFixed(2))}</b>,{" "}
							{eProps.events?.length}x Pt. - {surgeonAcronym}
						</>
					) : (
						`$${estimate.toFixed(2)}, ${
							eProps.events?.length
						}x Pt. - ${surgeonAcronym}`
					)
				) : isDayGridMonth ? (
					`N/A, ${eProps.events?.length}x Pt. - ${surgeonAcronym}`
				) : (
					`N/A`
				),
				backgroundColour: setEventColor(
					userSettings,
					"feeEstimate",
					eProps,
					schedules,
				),
			};
		}
		case "reconciliation": {
			const estimate = calculateFeeEstimate(
				userSettings,
				eProps.events,
				schedules,
			);

			return {
				content: isDayGridMonth ? (
					`Paid $- Est: $${estimate?.toFixed(2)}`
				) : isTimeGridWeek ? (
					<>
						Paid $-
						<br />
						Estimate: $${estimate?.toFixed(2)}
					</>
				) : isTimeGridDay ? (
					timeGridDayTemplate // Use the reusable template
				) : isListView ? (
					`Paid $- Est: $${estimate?.toFixed(2)}`
				) : (
					`${eventInfo.timeText} ${eventInfo.event.title}`
				),
				backgroundColour: setEventColor(
					userSettings,
					"reconciliation",
					eProps,
				),
			};
		}
		case "regularAdhoc": {
			return {
				content: isDayGridMonth ? (
					`${eventInfo.timeText} - ${surgeonAcronym} - ${hospitalAcronym}`
				) : isTimeGridWeek ? (
					<>
						{eventInfo.timeText} <br />
						{surgeonName} <br />
						{hospitalName.name}
					</>
				) : isTimeGridDay ? (
					timeGridDayTemplate // Use the reusable template
				) : isListView ? (
					`${eventInfo.timeText} - ${hospitalAcronym} - ${surgeonAcronym}`
				) : (
					`${eventInfo.timeText} ${eventInfo.event.title}`
				),
				backgroundColour: setEventColor(
					userSettings,
					"regularAdhoc",
					eProps,
				),
			};
		}
		case "fundingStatus": {
			const colourWithAlpha = (colour: string, alphaHex = "40") => {
				if (!colour) return "transparent";

				if (colour.startsWith("#")) {
					return `${colour}${alphaHex}`;
				}

				if (colour.startsWith("rgb(")) {
					return colour
						.replace("rgb(", "rgba(")
						.replace(")", ", 0.25)");
				}

				return colour;
			};

			/* =============================
			 * SESSION FUNDING STATUS
			 * ============================= */

			const sessionFundingStatus = determineSessionFundingStatus(
				eProps.events,
			);

			console.debug("[Funding] Session status:", {
				sessionId: eventInfo.event.id,
				status: sessionFundingStatus,
			});

			const fundingColours =
				userSettings?.calendar?.colorConfig?.fundingStatus || {};

			/*
			 * Keep backwards compatibility with the previous colour config.
			 *
			 * Priority:
			 * 1. IFC required         -> red
			 * 2. Funding incomplete   -> amber
			 * 3. Funding complete     -> green
			 */
			const ifcRequiredColour =
				fundingColours["IFC Required"] || "#d32f2f";

			const fundingIncompleteColour =
				fundingColours["Funding Incomplete"] ||
				fundingColours["Funding Missing"] ||
				"#ed6c02";

			const fundingCompleteColour =
				fundingColours["Funding Complete"] ||
				fundingColours["Funding Available"] ||
				"#388e3c";

			const getFundingColour = (status) => {
				switch (status) {
					case "ifc_required":
						return ifcRequiredColour;

					case "funding_incomplete":
						return fundingIncompleteColour;

					case "complete":
						return fundingCompleteColour;

					default:
						return "grey";
				}
			};

			const getFundingStatusLabel = (status) => {
				switch (status) {
					case "ifc_required":
						return "IFC Required";

					case "funding_incomplete":
						return "Funding Incomplete";

					case "complete":
						return "Funding Complete";

					default:
						return "Funding Status Unknown";
				}
			};

			/* =============================
			 * PATIENT FUNDING DETAILS
			 * ============================= */

			const getPatientFundingMissing = (subEvent) => {
				const missing: string[] = [];

				if (!subEvent.patient?.medicare) {
					missing.push("MC");
				}

				if (!subEvent.patient?.policies?.[0]?.healthFund?.id) {
					missing.push("HF");
				}

				return missing;
			};

			const getPatientFundingColour = (subEvent) => {
				const status = determineEventFundingStatus(subEvent);

				return getFundingColour(status);
			};

			const getPatientFundingLabel = (subEvent) => {
				const status = determineEventFundingStatus(subEvent);

				if (status === "ifc_required") {
					return "IFC Required";
				}

				if (status === "funding_incomplete") {
					const missing = getPatientFundingMissing(subEvent);

					return missing.length
						? `Missing: ${missing.join(", ")}`
						: "Funding Incomplete";
				}

				if (status === "complete") {
					return "Funding Complete";
				}

				return "Funding Status Unknown";
			};

			/* =============================
			 * SESSION COLOUR
			 * ============================= */

			const sessionColour = eProps?.isRegularSession
				? "#808080"
				: getFundingColour(sessionFundingStatus);

			const transparentSessionColour = colourWithAlpha(
				sessionColour,
				"40",
			);

			/* =============================
			 * DAY VIEW
			 * ============================= */

			const fundingTimeGridDayTemplate = (
				<div
					className="event-multi-box"
					style={{
						backgroundColor: transparentSessionColour,
					}}
				>
					<div className="event-header">
						<div className="event-info">
							{eProps.startingTime.replace(":", "")} -
							<b>
								{` ${eProps.surgeon.first_name} ${eProps.surgeon.last_name}`}
							</b>{" "}
							- {eProps.hospital.name}
						</div>
					</div>

					{eProps.events.map((subEvent, index) => {
						const patientColour = getPatientFundingColour(subEvent);

						return (
							<div
								key={subEvent.id || index}
								className="event-box"
								style={{
									height: `${100 / eProps.events.length}%`,
									backgroundColor: patientColour,
								}}
							>
								<div>
									<b>
										{subEvent.patient?.title}{" "}
										{subEvent.patient?.first_name}{" "}
										{subEvent.patient?.last_name}
									</b>
									{` ${
										subEvent.patient?.date_of_birth || ""
									}`}
								</div>

								<div>{getPatientFundingLabel(subEvent)}</div>
							</div>
						);
					})}
				</div>
			);

			/* =============================
			 * WEEK VIEW
			 * ============================= */

			const fundingTimeGridWeekTemplate = (
				<div
					className="event-multi-box"
					style={{
						backgroundColor: transparentSessionColour,
					}}
				>
					<div className="event-header">
						<div className="event-info">
							{eventInfo.timeText}
							<br />
							<b>{surgeonName}</b>
						</div>
					</div>

					{eProps.events.map((subEvent, index) => {
						const patientColour = getPatientFundingColour(subEvent);

						return (
							<div
								key={subEvent.id || index}
								className="event-box"
								style={{
									height: `${100 / eProps.events.length}%`,
									backgroundColor: patientColour,
								}}
							>
								<div>
									<b>
										{subEvent.patient?.first_name}{" "}
										{subEvent.patient?.last_name}
									</b>
								</div>

								<div>{getPatientFundingLabel(subEvent)}</div>
							</div>
						);
					})}
				</div>
			);

			/* =============================
			 * RENDER
			 * ============================= */

			const sessionFundingLabel =
				getFundingStatusLabel(sessionFundingStatus);

			return {
				content: isDayGridMonth
					? `${sessionFundingLabel} - ${eProps.events?.length}x Pt.`
					: isTimeGridWeek
						? fundingTimeGridWeekTemplate
						: isTimeGridDay
							? fundingTimeGridDayTemplate
							: isListView
								? `${surgeonName} - ${sessionFundingLabel}`
								: `${eventInfo.timeText} ${eventInfo.event.title}`,

				backgroundColour: sessionColour,
			};
		}
		case "speciality": {
			return {
				content: isDayGridMonth
					? `${surgeonAcronym}`
					: isTimeGridWeek
						? `${surgeonName}`
						: isTimeGridDay
							? timeGridDayTemplate // Use the reusable template
							: isListView
								? `${surgeonName}`
								: `${eventInfo.timeText} ${eventInfo.event.title}`,
				backgroundColour: setEventColor(
					userSettings,
					"speciality",
					eProps,
				),
			};
		}
		case "tag": {
			return {
				content: isDayGridMonth ? (
					`${surgeonAcronym} - ${hospitalAcronym}`
				) : isTimeGridWeek ? (
					<>
						{" "}
						TAG <br />
						{surgeonName} <br />
						{hospitalName.name}
					</>
				) : isTimeGridDay ? (
					timeGridDayTemplate // Use the reusable template
				) : isListView ? (
					`${surgeonAcronym} - ${hospitalAcronym}`
				) : (
					`${eventInfo.timeText} ${eventInfo.event.title}`
				),
				backgroundColour: setEventColor(userSettings, "tag", eProps),
			};
		}
		case "IFC": {
			return {
				content: isDayGridMonth ? (
					`${eProps.events?.length}x Pt. - ${surgeonAcronym}`
				) : isTimeGridWeek ? (
					<>
						{eProps.events?.length}x Patient
						{eProps.events?.length > 1 ? "s" : ""} <br />
						{surgeonName}
					</>
				) : isTimeGridDay ? (
					timeGridDayTemplate // Use the reusable template
				) : isListView ? (
					`${surgeonAcronym} - ${hospitalAcronym}`
				) : (
					`${eventInfo.timeText} ${eventInfo.event.title}`
				),
				backgroundColour: setEventColor(userSettings, "IFC", eProps),
			};
		}
		default:
			return {
				content: isDayGridMonth
					? `${hospitalAcronym}`
					: isTimeGridWeek
						? `${eventInfo.timeText} - ${hospitalAcronym}`
						: isTimeGridDay
							? timeGridDayTemplate // Use the reusable template
							: isListView
								? `Event: ${eventInfo.event.title}`
								: `${eventInfo.timeText} ${eventInfo.event.title}`,
				backgroundColour: "grey",
			};
	}
};

// Formats the events into a uniform structure for use in rendering.
// Applies alias override here,
export const formatEvents = (view, eventsArray, user, schedules) => {
	const { aliases } = user.settings;

	const sortedEventsArray = [...eventsArray].sort((a, b) => {
		const dateA = new Date(a.date_of_service + "T" + a.start_time);
		const dateB = new Date(b.date_of_service + "T" + b.start_time);
		return dateA - dateB;
	});

	const formattedEvents = sortedEventsArray.map((event, index) => {
		const startDate = event.date_of_service;
		const startTime = event.start_time ? `T${event.start_time}:00` : "";

		const endDateTime = event.end_time
			? `${startDate}T${event.end_time}:00`
			: "";

		// Alias management for hospital and surgeon
		const hospitalAlias = aliases?.hospital_aliases?.find(
			(alias) => alias.name === event.hospital.name,
		);
		const hospitalDisplayName = hospitalAlias
			? hospitalAlias.preferred_name
			: event.hospital.name;
		const hospitalAcronym = hospitalAlias?.preferred_acronym || "";

		const surgeonAlias = aliases?.surgeon_aliases?.find(
			(alias) =>
				alias.name ===
				`${event.surgeon.first_name} ${event.surgeon.last_name}`,
		);
		const surgeonAcronym = surgeonAlias?.preferred_acronym || "";

		// Process sub-events with health fund aliases and fee estimates
		const processedEvents = event.events.map((subEvent) => {
			const feeEstimate = calculateSinglePtFeeEstimate(
				user.settings,
				subEvent,
				schedules,
			);

			const healthFund = subEvent.patient?.policies[0]?.healthFund;
			const healthFundAlias = healthFund
				? aliases?.healthfund_aliases?.find(
						(alias) => alias.healthfund_id === healthFund.id,
					)
				: null;

			const healthFundDisplayName = healthFundAlias
				? healthFundAlias.preferred_name
				: healthFund?.aliases[1] || "N/A";
			const healthFundAcronym = healthFundAlias?.preferred_acronym || "";

			return {
				...subEvent,
				feeEstimate,
				patient: {
					...subEvent.patient,
					policies: [
						{
							...subEvent.patient.policies[0],
							healthFund: {
								...healthFund,
								acronym: healthFundAcronym,
								name: healthFundDisplayName,
							},
						},
					],
				},
			};
		});
		// Check for overlap with the next event for better time management
		const nextEvent = sortedEventsArray[index + 1];

		const formattedEvent = {
			id: event.id,
			title: toCapitalized(event.hospital.name),
			start: startDate + startTime,
			end:
				endDateTime ||
				calculateEndTime(startDate, event.start_time, nextEvent),
			surgeon: {
				id: event.surgeon.id,
				acronym: surgeonAcronym,
				first_name: surgeonAlias
					? surgeonAlias.preferred_name.split(" ")[0]
					: event.surgeon.first_name,
				last_name: surgeonAlias
					? surgeonAlias.preferred_name.split(" ")[1]
					: event.surgeon.last_name,
				specialty: event.surgeon?.specialty ?? [],
			},
			hospital: {
				id: event.hospital.id,
				acronym: hospitalAcronym,
				name: hospitalDisplayName,
				dbName: event.hospital.name,
			},
			events: processedEvents,
			startingTime: event.start_time,

			// Preserve regular-session metadata
			isRegularSession: Boolean(event.isRegularSession),
			week_cycle: event.week_cycle ?? null,
			week_day: event.week_day ?? null,
			am: Boolean(event.am),
			pm: Boolean(event.pm),
			tw: Boolean(event.tw),

			// Check if session is linked to RS
			regular_session_id: event.regular_session_id ?? null,
		};

		return {
			...formattedEvent,
			color: setEventColor(
				user.settings,
				view,
				formattedEvent,
				schedules,
			),
		};
	});
	// // Apply pattern matching and future event predictions
	// const predictedEvents = detectRecurringEvents(formattedEvents);

	// Combine existing and predicted events
	return [
		...formattedEvents,
		// , ...predictedEvents
	];
};

// Determine and set the event block colour.
export const setEventColor = (userSettings, view, e, schedules?) => {
	const userColorConfig = userSettings?.calendar?.colorConfig?.[view] || {};

	switch (view) {
		case "main": {
			if (e?.isPersonalEvent) {
				return e?.isRegularPersonal
					? userColorConfig["Personal Regular"] || "#757575"
					: userColorConfig["Personal Adhoc"] || "#9e9e9e";
			}

			// Captures both generated regular sessions, and sessions that are linked to RS.
			const isWorkRegular =
				Boolean(e?.isRegularSession) || Boolean(e?.regular_session_id);

			if (isWorkRegular) {
				return userColorConfig["Work Regular"] || "#1976d2";
			}

			return userColorConfig["Work Adhoc"] || "#3ccf44";
		}

		case "uploadStatus":
			return userColorConfig["Upload Complete"] || "#388e3c";

		case "feeEstimate":
			const estimate = calculateFeeEstimate(
				userSettings,
				e.events,
				schedules,
			);
			return estimate
				? userColorConfig["Estimate"] || "#388e3c"
				: userColorConfig["No Estimate"] || "grey";
		case "reconciliation":
			// Check reconciliation - need to add fn here
			const reconciliation = true;
			return reconciliation
				? userColorConfig["Reconciled"] || "#388e3c"
				: userColorConfig["Not Reconciled"] || "grey";
		case "fundingStatus": {
			const fundingStatus = determineSessionFundingStatus(e.events);

			console.debug("[Funding] setEventColor:", {
				sessionId: e?.id,
				status: fundingStatus,
			});

			switch (fundingStatus) {
				case "ifc_required":
					return userColorConfig["IFC Required"] || "#d32f2f";

				case "funding_incomplete":
					return (
						userColorConfig["Funding Incomplete"] ||
						userColorConfig["Funding Missing"] ||
						"#ed6c02"
					);

				case "complete":
					return (
						userColorConfig["Funding Complete"] ||
						userColorConfig["Funding Available"] ||
						"#388e3c"
					);

				default:
					return "grey";
			}
		}
		case "speciality": {
			const primary = getPrimarySurgeonSpeciality(e);
			if (!primary) {
				console.debug("[CAL] speciality: none found → grey");
				return "grey";
			}

			const cfg = userColorConfig || {};
			// case-insensitive lookup to be forgiving with capitalisation
			const cfgLower = new Map(
				Object.entries(cfg).map(([k, v]) => [
					k.toLowerCase(),
					v as string,
				]),
			);
			const chosen =
				cfg[primary] ?? cfgLower.get(primary.toLowerCase()) ?? null;

			console.debug("[CAL] speciality:", {
				primary,
				chosen: chosen ?? "grey",
			});
			return chosen || "grey";
		}
		case "tag":
			// Check tag - need to add fn here
			// const tag = true;
			return "#388e3c";

		case "IFC":
			// Check IFC sent - need to add fn here
			const sent = true;
			return sent
				? userColorConfig["IFC Sent"] || "#388e3c"
				: userColorConfig["IFC Not Sent"] || "grey";
		default:
			return "grey";
	}
};

const abbreviateName = (name) => {
	return name
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase())
		.join("");
};

// Convert month view time text to 24-hour format
const formatMonthlyViewTime = (timeText) => {
	if (!timeText) return "";
	const timeRegex = /^(\d{1,2}):?(\d{2})?(a|p)$/;
	const match = timeText.match(timeRegex);
	if (match) {
		let [hours, minutes = "00", period] = [match[1], match[2], match[3]];
		hours = parseInt(hours, 10);
		if (period === "p" && hours !== 12) {
			hours += 12;
		} else if (period === "a" && hours === 12) {
			hours = 0;
		}
		return `${hours.toString().padStart(2, "0")}${minutes}`;
	}
	return timeText; // Return as-is if not matched
};

const toCapitalized = (str) => {
	return str
		.split(" ")
		.map(
			(word) =>
				word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
		)
		.join(" ");
};

// Calculate end time for each calendar event to display block length in weekly view.
export const calculateEndTime = (startDate, startTime, nextEvent) => {
	if (!startTime) {
		return null;
	}

	const startHour = parseInt(startTime.split(":")[0], 10);
	const startMinute = parseInt(startTime.split(":")[1], 10);
	const startDateTime = new Date(`${startDate}T${startTime}`);

	let defaultEndTime;

	// Determine the default end time based on start time ranges
	if (
		(startHour >= 6 && startHour < 11) ||
		(startHour === 11 && startMinute <= 30)
	) {
		defaultEndTime = new Date(`${startDate}T18:00:00`);
	} else if (
		(startHour >= 12 && startHour < 17) ||
		(startHour === 17 && startMinute <= 30)
	) {
		defaultEndTime = new Date(`${startDate}T22:00:00`);
	} else if (startHour >= 17 && startHour < 20) {
		defaultEndTime = new Date(`${startDate}T22:00:00`);
	} else {
		defaultEndTime = new Date(startDateTime);
	}

	// Check for overlap with the next event
	if (nextEvent) {
		const nextEventStartDateTime = new Date(
			`${nextEvent.date_of_service}T${nextEvent.start_time}:00`,
		);
		if (defaultEndTime > nextEventStartDateTime) {
			// If the default end time causes an overlap, apply the overlap rules
			if (
				(startHour >= 6 && startHour < 11) ||
				(startHour === 11 && startMinute <= 30)
			) {
				defaultEndTime = new Date(`${startDate}T12:30:00`);
			} else if (
				(startHour >= 12 && startHour < 17) ||
				(startHour === 17 && startMinute <= 30)
			) {
				defaultEndTime = new Date(`${startDate}T17:30:00`);
			}
		}
	}
	// Convert UTC to local time directly using Date methods
	const localEndTime = new Date(
		defaultEndTime.getTime() - defaultEndTime.getTimezoneOffset() * 60000,
	);

	return localEndTime.toISOString().slice(0, -1); // Remove the 'Z' to reflect local time without the UTC indicator
};

const getPrimarySurgeonSpeciality = (eProps: any): string | null => {
	const arr = eProps?.surgeon?.specialty;
	if (Array.isArray(arr) && arr.length) {
		const s = String(arr[0]).trim();
		return s || null;
	}
	return null;
};
